import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { AdvancedFilter } from "../../models/advanced-filter.interface";
import {
  AdvancedFilterFieldInfo,
  ComparisonOperator,
  getOperatorsByKey
} from "../../models/advanced-filter-config.interface";
import { AutocompleteDropdownComponent } from "../autocomplete-dropdown/autocomplete-dropdown.component";
import { autocompleteValidator } from "../../validators/autoCompleteValidator";
import { MultiSelectComponent } from "../select/multi-select.component";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { MaterialModule } from "../../modules/material/material.module";
import { ToggleComponent } from "../common/toggle/toggle.component";
import { DatePickerComponent } from "../common/date-picker/date-picker.component";

export interface AdvancedFilterOutput {
  refresh?: boolean;
  rawFilterData?: any;
  filterData?: AdvancedFilter;
  resetAll?: boolean;
}

@Component({
  selector: "app-advanced-filter-v2",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SvgIconComponent,
    AutocompleteDropdownComponent,
    MultiSelectComponent,
    ScrollingModule,
    MaterialModule,
    ToggleComponent,
    DatePickerComponent
  ],
  templateUrl: "./advanced-filter-v2.component.html",
  styleUrl: "./advanced-filter-v2.component.scss"
})
export class AdvancedFilterComponentV2 {
  @Input() appliedFilters!: AdvancedFilter;
  @Input() filterFields!: AdvancedFilterFieldInfo[];
  @Input() useNewContract: boolean = false;
  @Input() view: 'panelView' | 'modalView' = 'modalView';

  filterForm: FormGroup;
  aliasCounter = 0;

  operatorsMap: { [key: string]: ComparisonOperator[] } = {
    STRING: [
      ComparisonOperator.STARTS_WITH,
      ComparisonOperator.EQ,
      ComparisonOperator.NE
    ],
    NUMBER: [
      ComparisonOperator.EQ,
      ComparisonOperator.NE,
      ComparisonOperator.LT,
      ComparisonOperator.GT
    ],
    BOOLEAN: [ComparisonOperator.EQ],
    PICKLIST: [ComparisonOperator.IN, ComparisonOperator.NOT_IN],
    LOOKUP: [ComparisonOperator.EQ, ComparisonOperator.NE]
  };

  logicalOperators = ["AND", "OR"];

  fieldOperatorMap: Map<String, ComparisonOperator[]> = new Map();
  fieldInfoMap: Map<String, AdvancedFilterFieldInfo> = new Map();

  constructor(private fb: FormBuilder) {
    this.filterForm = this.fb.group({
      filterConditions: this.fb.array([]),
      finalExpression: this.fb.array([]),
      expression: [""]
    });
  }

  closeModal!: (params?: any) => void;

  closeRightPanel!:(params?: any) => void;

  close(params?: any) {
    if (this.view==='modalView' && this.closeModal) {
      this.closeModal(params);
    }else if(this.view === 'panelView' && this.closeRightPanel) {
      this.closeRightPanel(params);
    }
  }

  ngOnInit() {
    if (this.appliedFilters) {
      console.log("setting filters: ", this.appliedFilters);
      this.setFilters(this.appliedFilters);
    } else {
      this.addCondition();
    }

    this.filterForm.get("expression")?.valueChanges.subscribe((value) => {
      console.log("Textarea updated:", value);
    });

    this.populateFieldOperators();
  }

  get filterConditions(): FormArray {
    return this.filterForm.get("filterConditions") as FormArray;
  }

  getFieldInfo(fieldName: string) {
    return this.fieldInfoMap.get(fieldName);
  }

  get finalExpression(): FormArray {
    return this.filterForm.get("finalExpression") as FormArray;
  }

  private populateFieldOperators() {
    this.filterFields.forEach((fieldInfo: AdvancedFilterFieldInfo) => {
      const dataType = fieldInfo?.hasLookup ? "LOOKUP" : fieldInfo?.dataType || "STRING";
      let supportedOperators = getOperatorsByKey(fieldInfo?.supportedOperators || []);
      if (!this.useNewContract) {
        supportedOperators = supportedOperators || this.operatorsMap[dataType];
      } else {
        const keys = Array.from(fieldInfo.operatorToFilterMap?.keys() || []);
        supportedOperators = getOperatorsByKey(keys);
      }
      this.fieldOperatorMap.set(fieldInfo.name, supportedOperators || []);
      this.fieldInfoMap.set(fieldInfo.name, fieldInfo);
    });
    console.log(this.fieldOperatorMap);
  }

  generateAlias(): string {
    const baseChar = 'A'.charCodeAt(0);
    let alias = '';
    let counter = this.aliasCounter;

    while (counter >= 0) {
      alias = String.fromCharCode(baseChar + (counter % 26)) + alias;
      counter = Math.floor(counter / 26) - 1;
    }

    this.aliasCounter++;
    return alias;
  }

  addCondition() {
    const alias = this.generateAlias();
    this.filterConditions.push(
      this.fb.group({
        name: ["", Validators.required],
        alias: [{ value: alias, disabled: true }, Validators.required],
        operator: [{ value: "", disabled: true }, Validators.required],
        value: [{ value: [], disabled: true }, Validators.required]
        //value: [{ value: '', disabled: true }, Validators.required],
      })
    );

    if (this.finalExpression.length > 0) {
      this.finalExpression.push(
        this.fb.group({
          type: "operator",
          value: "AND" // Default operator
        })
      );
    }

    this.finalExpression.push(
      this.fb.group({
        type: "alias",
        value: alias
      })
    );

    this.updateExpressionString();
  }

  removeCondition(index: number) {
    const alias = this.filterConditions.at(index).get("alias")?.value;
    this.filterConditions.removeAt(index);

    // Find the alias in finalExpression
    const aliasIndex = this.finalExpression.controls.findIndex(
      (group) =>
        group.get("value")?.value === alias &&
        group.get("type")?.value === "alias"
    );

    if (aliasIndex !== -1) {
      this.finalExpression.removeAt(aliasIndex); // Remove alias

      // Remove preceding logical operator if exists (and not the first element)
      if (aliasIndex >= 0 && this.finalExpression.at(aliasIndex)?.get("type")?.value === "operator") {
        this.finalExpression.removeAt(aliasIndex);
      }

      // If the last remaining element is a logical operator, remove it
      if (this.finalExpression.length > 0 && this.finalExpression.at(this.finalExpression.length - 1)?.get("type")?.value === "operator") {
        this.finalExpression.removeAt(this.finalExpression.length - 1);
      }
    }

    this.updateAliases();
    this.updateExpressionString();
  }

  // updateExpressionString() {
  //   console.log('updateExpressionString');
  //   const expressionString = this.finalExpression.controls
  //     .map((group) => group.get('value')?.value)
  //     .join(' ');
  //   this.filterForm.get('expression')?.setValue(expressionString);

  //   //   let processedExpressionString;
  //   //   if (expressionString.length) {
  //   //     processedExpressionString = '( ' + expressionString + ' )';
  //   //   }
  //   //   this.filterForm.get('expression')?.setValue(processedExpressionString);
  // }

  // // updateAliases() {
  // //   this.aliasCounter = 0;
  // //   this.filterConditions.controls.forEach((group) => {
  // //     group.get('alias')?.setValue(this.generateAlias());
  // //   });
  // // }

  updateExpressionString() {
    const expressionString = this.finalExpression.controls
      .map((group) => group.get("value")?.value)
      .join(" ");

    // Set the updated expression to the filter form
    this.filterForm.get("expression")?.setValue(expressionString);

    // Optionally wrap in parentheses for clearer grouping
    const processedExpressionString = `(${expressionString})`;
    this.filterForm.get("expression")?.setValue(processedExpressionString);
  }

  //below is new alias used mapping of key value key of old alias and value new alias more effiecient
  updateAliases() {
    let aliasMapping: { [oldAlias: string]: string } = {};
    let newAliasCounter = 0;

    this.filterConditions.controls.forEach((group) => {
      const oldAlias = group.get("alias")?.value;
      const newAlias = this.generateAlias();

      aliasMapping[oldAlias] = newAlias;
      group.get("alias")?.setValue(newAlias);
    });

    // Update finalExpression with new alias mapping
    this.finalExpression.controls.forEach((group) => {
      const value = group.get("value")?.value;
      if (group.get("type")?.value === "alias" && aliasMapping[value]) {
        group.get("value")?.setValue(aliasMapping[value]);
      }
    });

    this.updateExpressionString();
  }

  //below is old alias which rely on find function
  // updateAliases() {
  //   let currentAliasIndex = 0;

  //   // Update aliases for filterConditions
  //   this.filterConditions.controls.forEach((group, index) => {
  //     const newAlias = this.generateAlias();
  //     group.get('alias')?.setValue(newAlias);

  //     // Update alias in finalExpression too
  //     const aliasIndex = this.finalExpression.controls.findIndex(
  //       (exprGroup) =>
  //         exprGroup.get('value')?.value === group.get('alias')?.value
  //     );

  //     if (aliasIndex !== -1) {
  //       this.finalExpression.at(aliasIndex).get('value')?.setValue(newAlias);
  //     }

  //     currentAliasIndex++;
  //   });

  //   // Rebuild the expression string after alias changes
  //   this.updateExpressionString();
  // }

  applyFilter() {
    this.filterForm.markAllAsTouched();
    if (this.filterForm.invalid) {
      return;
    }

    //this.cleanUnfilledConditions(); // Clean unfilled conditions before applying filter
    const filterData = this.getProcessedFilterData();
    console.log(filterData);

    const filterDataOutput: AdvancedFilterOutput = {
      refresh: true,
      rawFilterData: this.filterForm.getRawValue(),
      filterData
    };
    this.closeView(filterDataOutput);
  }

  private closeView(filterDataOutput: AdvancedFilterOutput) {
    this.view === "modalView" ? this.closeModal(filterDataOutput) : this.closeRightPanel(filterDataOutput);
  }

  getProcessedFilterData() {
    // Get the raw values of the form
    const filterData = this.filterForm.getRawValue();

    // Iterate through filterConditions and process each condition
    filterData.filterConditions = filterData.filterConditions.map(
      (condition: any) => {
        const fieldInfo = this.fieldInfoMap.get(condition.name);
        condition.value = Array.isArray(condition.value) ? condition.value : [condition.value];

        if (this.useNewContract) {
          console.log("condition.operator :", fieldInfo?.operatorToFilterMap);
          condition.name =
            fieldInfo?.operatorToFilterMap?.get(condition.operator) ||
            condition.name;
          condition.operator = null;
        }
        return condition;
      }
    );

    return filterData;
  }

  // cleanUnfilledConditions() {
  //   const filledConditions = this.filterConditions.controls.filter(
  //     (control, index) => {
  //       const name = control.get('name')?.value;
  //       const operator = control.get('operator')?.value;
  //       const valuesArray = control.get('value') as FormArray;
  //       const values = valuesArray.value;

  //       if (name && operator && values.every((value: string) => !!value)) {
  //         return true;
  //       } else {
  //         // Remove corresponding alias and operator from the finalExpression
  //         const alias = control.get('alias')?.value;
  //         let aliasIndex = this.finalExpression.controls.findIndex(
  //           (group) =>
  //             group.get('value')?.value === alias &&
  //             group.get('type')?.value === 'alias'
  //         );

  //         if (aliasIndex !== -1) {
  //           this.finalExpression.removeAt(aliasIndex);
  //           if (
  //             aliasIndex > 0 &&
  //             this.finalExpression.at(aliasIndex - 1)?.get('type')?.value ===
  //               'operator'
  //           ) {
  //             this.finalExpression.removeAt(aliasIndex - 1);
  //           }
  //         }

  //         return false;
  //       }
  //     }
  //   );

  //   this.filterForm.setControl(
  //     'filterConditions',
  //     this.fb.array(filledConditions)
  //   );
  //   this.updateExpressionString(); // Update the expression string after cleaning
  // }

  setFilters(filterData: any) {
    console.log("setting filters now");
    const filterConditions = filterData.filterConditions || [];
    const expressionArray = filterData.expression
      ? filterData.expression.split(" ")
      : [];

    // Clear existing filter conditions and final expression
    this.filterConditions.clear();
    this.finalExpression.clear();
    this.aliasCounter = 0;

    // Track the maximum alias index found in filter data
    let maxAliasIndex = -1;

    // Loop through each filter condition from the provided filterData
    filterConditions.forEach((condition: any) => {
      const value = Array.isArray(condition.value) ? condition.value : [condition.value]

      const finalValue = value.length === 1 ? value[0] : value;
      
      const conditionGroup = this.fb.group({
        name: [condition.name, Validators.required],
        alias: [
          { value: condition.alias, disabled: true },
          Validators.required
        ],
        operator: ["", Validators.required],
        value: ["", Validators.required]
      });

      this.filterConditions.push(conditionGroup);

      // Set the operator and value after the group is created
      // This ensures ControlValueAccessor components properly receive the values
      setTimeout(() => {
        conditionGroup.patchValue({
          operator: condition.operator,
          value: finalValue
        }, { emitEvent: false });
      }, 0);

      // Push the alias to the finalExpression
      this.finalExpression.push(
        this.fb.group({
          type: "alias",
          value: condition.alias
        })
      );

      // Determine the alias index from its last character if it's numeric
      const aliasLastChar = condition.alias.slice(-1);
      const aliasIndex = isNaN(Number(aliasLastChar))
        ? condition.alias.charCodeAt(0) - "A".charCodeAt(0)
        : Number(aliasLastChar) +
        26 * (condition.alias.charCodeAt(0) - "A".charCodeAt(0));

      if (aliasIndex > maxAliasIndex) {
        maxAliasIndex = aliasIndex;
      }
    });

    // Loop through the expressionArray and fill finalExpression with operators
    for (let i = 0; i < expressionArray.length; i++) {
      if (this.logicalOperators.includes(expressionArray[i])) {
        this.finalExpression.insert(
          i,
          this.fb.group({
            type: "operator",
            value: expressionArray[i]
          })
        );
      }
    }

    // Set aliasCounter to the next available alias index after maxAliasIndex
    this.aliasCounter = maxAliasIndex + 1;

    // Update the final expression string
    this.updateExpressionString();
  }

  // Helper function to set aliasCounter based on the last alias used
  incrementAliasCounter(alias: string) {
    const alphabet = alias.replace(/[0-9]/g, "");
    const number = parseInt(alias.replace(alphabet, ""), 10) || 0;
    this.aliasCounter =
      alphabet.charCodeAt(0) - "a".charCodeAt(0) + number * 26 + 1;
  }

  onOperatorChange(index: number, operator: string) {
    this.finalExpression.at(index).get("value")?.setValue(operator);
    this.updateExpressionString();
  }

  resetFilter() {
    console.log('clearFilters');
    const resetFilterData: AdvancedFilterOutput = {
      refresh: true,
      resetAll: true,
    };
    this.closeView(resetFilterData);
  }

  fieldSelection(selectedField: AdvancedFilterFieldInfo | string, index: number) {
    // console.log ("Selected Field and Index are",selectedField,index)
    const condition = this.filterConditions.at(index);
    const fieldInfo = this.filterFields.find((field) =>
      typeof selectedField === "string" ? field.name === selectedField : field.name === selectedField.name
    );

    if (fieldInfo) {
      const operator = condition.get("operator")
      const value = condition.get("value");
      if(!value || !operator) return ;

      operator.enable();
      operator.setValue("");

      if(operator.enabled) {
        value.setValue("");
        value.enable()
      }

      this.addValueControls(value, fieldInfo);
      
      if (fieldInfo?.operatorToFilterMap?.size === 1) {
        condition.get("operator")?.setValue(Array.from(fieldInfo?.operatorToFilterMap?.keys())[0] as string);
      }
    } else {
      condition.get("operator")?.disable();
      const valueArray = condition.get("value") as FormArray;
      valueArray.clear();
    }
    console.log("post field selection");
  }

  addValueControls(value: any, fieldInfo: AdvancedFilterFieldInfo) {
        if (fieldInfo.hasLookup) {
          value.setValidators(Validators.required);
        } else {
          value.setValidators(Validators.required);
        }
  }
}
